import type { Me } from "@riea-water/contracts";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { toggleTheme } from "./theme.js";

export const ROLE_LABELS: Record<Me["role"], string> = {
  tenant_admin: "Tenant Administrator",
  data_lead: "Market Data Lead",
  analyst: "Market Services Analyst",
  field_coordinator: "Field Coordinator",
  auditor: "Auditor",
};

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** The realm roles in an access token (realm_access.roles), or none when it cannot be read. */
export function tokenRealmRoles(token: string | null): string[] {
  const payload = token?.split(".")[1];
  if (!payload) return [];
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const roles = (JSON.parse(json) as { realm_access?: { roles?: unknown } }).realm_access?.roles;
    return Array.isArray(roles) ? roles.map(String) : [];
  } catch {
    return [];
  }
}

/** Links to the sibling products: RIEA Pro when configured, the head-end only for a token with the hes role. */
function ProductLinks(props: {
  links?: { rieaPro?: string | undefined; hes?: string | undefined } | undefined;
  getToken?: (() => Promise<string | null>) | undefined;
}) {
  const { links, getToken } = props;
  const [hesAllowed, setHesAllowed] = useState(false);
  useEffect(() => {
    if (!links?.hes || !getToken) return;
    let live = true;
    void getToken().then((token) => {
      if (live) setHesAllowed(tokenRealmRoles(token).includes("hes"));
    });
    return () => {
      live = false;
    };
  }, [links?.hes, getToken]);
  return (
    <>
      {links?.rieaPro ? (
        <a className="btn sm ghost" href={links.rieaPro} data-testid="nav-riea-pro">
          RIEA Pro
        </a>
      ) : null}
      {links?.hes && hesAllowed ? (
        <a className="btn sm ghost" href={links.hes} data-testid="nav-head-end">
          Head-end
        </a>
      ) : null}
    </>
  );
}

/** The prototype's top bar: wordmark, tenant chip, search, theme toggle, user and role, avatar, sign out. */
export function TopBar(props: {
  me: Me;
  onSignOut: () => void;
  links?: { rieaPro?: string | undefined; hes?: string | undefined } | undefined;
  getToken?: (() => Promise<string | null>) | undefined;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void navigate(`/supply-points?q=${encodeURIComponent(query.trim())}`);
  };
  return (
    <div className="top">
      <div className="wordmark" style={{ fontSize: "1.05rem" }}>
        <span className="mark" style={{ width: 22, height: 22 }}>
          <i style={{ width: 8, height: 8 }} />
        </span>
        RIEA Pro
      </div>
      <div className="tenant">
        {props.me.tenantName}{" "}
        <span className="chip">
          {props.me.partyRole === "wholesaler" ? "Wholesaler" : "Retailer"}
        </span>
      </div>
      <form className="search" role="search" onSubmit={submit}>
        <input
          type="search"
          id="gsearch"
          placeholder="Search SPID, UPRN, serial or address"
          aria-label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>
      <div className="who">
        <ProductLinks links={props.links} getToken={props.getToken} />
        <button
          className="iconbtn"
          type="button"
          onClick={() => toggleTheme()}
          aria-label="Toggle theme"
          title="Toggle theme"
        >
          ◐
        </button>
        <span className="nm" style={{ textAlign: "right" }}>
          <b>{props.me.name}</b>
          <br />
          <span className="faint" style={{ fontSize: ".72rem" }}>
            {ROLE_LABELS[props.me.role]} · {props.me.role}
          </span>
        </span>
        <span className="avatar" aria-hidden="true">
          {initials(props.me.name)}
        </span>
        <button className="btn sm ghost" type="button" onClick={props.onSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
}
